/**
 * Member Role API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  ATSMemberRole,
  ATSMemberRoleCreate,
  ATSMemberRoleUpdate,
} from '../types/member-role';

interface MemberRoleListApiResponse {
  success: boolean;
  data: ATSMemberRole[];
  current_user_role: string | null;
  current_user_account_id: string | null;
  message: string;
}

export interface MemberRoleListResponse {
  roles: ATSMemberRole[];
  currentUserRole: string | null;
  currentUserAccountId: string | null;
}

interface MemberRoleApiResponse {
  success: boolean;
  data: ATSMemberRole;
  message: string;
}

interface AvailableMember {
  account_id: string;
  account_name: string;
  account_email: string;
}

interface AvailableMembersApiResponse {
  success: boolean;
  data: AvailableMember[];
  total: number;
  message: string;
}

export interface AvailableMembersPage {
  data: AvailableMember[];
  total: number;
}

class MemberRoleService {
  /**
   * Get workspace members with ATS seats but no assigned roles.
   * These members can be assigned ATS roles.
   */
  async getAvailableMembers(
    workspaceId: string,
    idToken: string,
    options: { search?: string; skip?: number; limit?: number } = {}
  ): Promise<AvailableMembersPage> {
    const { search, skip = 0, limit = 20 } = options;
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<AvailableMembersApiResponse>(
      '/member-roles/available-members',
      {
        headers: { 'X-Workspace-ID': workspaceId },
        params: { search: search || undefined, skip, limit },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get available members');
    }

    return { data: response.data.data, total: response.data.total ?? response.data.data.length };
  }

  /**
   * List all ATS member roles in workspace.
   * Includes workspace owners with virtual MANAGER role.
   */
  async listMemberRoles(
    workspaceId: string,
    idToken: string
  ): Promise<MemberRoleListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<MemberRoleListApiResponse>('/member-roles', {
      headers: { 'X-Workspace-ID': workspaceId },
      params: { limit: 20 },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list member roles');
    }

    return {
      roles: response.data.data,
      currentUserRole: response.data.current_user_role,
      currentUserAccountId: response.data.current_user_account_id,
    };
  }

  /**
   * List recruiters with optional search and pagination.
   */
  async listRecruiters(
    workspaceId: string,
    idToken: string,
    options: { search?: string; skip?: number; limit?: number } = {}
  ): Promise<ATSMemberRole[]> {
    const { search, skip = 0, limit = 10 } = options;
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<MemberRoleListApiResponse>('/member-roles', {
      headers: { 'X-Workspace-ID': workspaceId },
      params: { role: 'recruiter', search, skip, limit },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list recruiters');
    }

    return response.data.data;
  }

  /**
   * Assign ATS role to a workspace member.
   * Cannot assign role to workspace owners (they have virtual MANAGER).
   */
  async assignMemberRole(
    data: ATSMemberRoleCreate,
    workspaceId: string,
    idToken: string
  ): Promise<ATSMemberRole> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<MemberRoleApiResponse>('/member-roles', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to assign role');
    }

    return response.data.data;
  }

  /**
   * Bulk assign ATS roles to multiple workspace members in one request.
   */
  async bulkAssignMemberRoles(
    assignments: Array<{ account_id: string; role: string }>,
    workspaceId: string,
    idToken: string
  ): Promise<{ assigned: ATSMemberRole[]; skipped: unknown[]; failed: unknown[] }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: ATSMemberRole[];
      skipped: unknown[];
      failed: unknown[];
      message: string;
    }>('/member-roles/bulk', { assignments }, { headers: { 'X-Workspace-ID': workspaceId } });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk assign roles');
    }

    return {
      assigned: response.data.data,
      skipped: response.data.skipped,
      failed: response.data.failed,
    };
  }

  /**
   * Update ATS role for a workspace member.
   * Cannot update role for workspace owners.
   */
  async updateMemberRole(
    accountId: string,
    data: ATSMemberRoleUpdate,
    workspaceId: string,
    idToken: string
  ): Promise<ATSMemberRole> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.put<MemberRoleApiResponse>(
      `/member-roles/${accountId}`,
      data,
      {
        headers: { 'X-Workspace-ID': workspaceId },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update role');
    }

    return response.data.data;
  }

  /**
   * Remove ATS role from a workspace member.
   * Cannot remove role from workspace owners.
   */
  async removeMemberRole(
    accountId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/member-roles/${accountId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const memberRoleService = new MemberRoleService();
export default memberRoleService;
export type { AvailableMember };
